import React, { useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useApp, api } from '@/context/AppContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  Brain, ChevronLeft, ChevronRight, Loader2, Trophy, Sparkles, CheckCircle2,
  Heart, Target, Zap, Award,
} from 'lucide-react';
import { toast } from 'sonner';

const DISABILITY_OPTS = [
  { id: 'visual', ar: 'إعاقة بصرية', en: 'Visual impairment' },
  { id: 'hearing', ar: 'إعاقة سمعية', en: 'Hearing impairment' },
  { id: 'intellectual', ar: 'إعاقة ذهنية', en: 'Intellectual disability' },
  { id: 'cp', ar: 'شلل دماغي', en: 'Cerebral palsy' },
  { id: 'limb', ar: 'بتر أطراف', en: 'Limb deficiency' },
  { id: 'wheelchair', ar: 'مستخدم كرسي متحرك', en: 'Wheelchair user' },
  { id: 'physical', ar: 'إعاقة جسدية أخرى', en: 'Other physical' },
];

const SCORE_AXES = [
  { key: 'strength',     ar: 'القوة العضلية',        en: 'Muscular strength',  icon: Zap },
  { key: 'speed',        ar: 'السرعة',                  en: 'Speed',              icon: Zap },
  { key: 'endurance',    ar: 'التحمّل',                  en: 'Endurance',          icon: Heart },
  { key: 'balance',      ar: 'التوازن',                  en: 'Balance',            icon: Target },
  { key: 'coordination', ar: 'التآزر الحركي',        en: 'Coordination',       icon: Target },
  { key: 'flexibility',  ar: 'المرونة',                  en: 'Flexibility',        icon: Target },
  { key: 'power',        ar: 'القوة الانفجارية',     en: 'Explosive power',    icon: Zap },
  { key: 'focus',        ar: 'التركيز الذهني',       en: 'Mental focus',       icon: Brain },
  { key: 'teamwork',     ar: 'العمل الجماعي',        en: 'Teamwork',           icon: Heart },
  { key: 'motivation',   ar: 'الدافعية',                 en: 'Motivation',         icon: Sparkles },
  { key: 'precision',    ar: 'الدقّة',                    en: 'Precision',          icon: Target },
  { key: 'reflex',       ar: 'سرعة رد الفعل',         en: 'Reflexes',           icon: Zap },
];

export default function TalentAssessment() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { lang, user } = useApp();
  const isAr = lang === 'ar';
  const initialAcademy = params.get('academy') || '';

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    full_name: user?.full_name || '',
    age: '',
    gender: '',
    disability_category: '',
    disability_details: '',
    academy_preference: initialAcademy,
    sport_interest: '',
    contact_phone: '',
    contact_email: user?.email || '',
    scores: Object.fromEntries(SCORE_AXES.map((a) => [a.key, 5])),
  });

  const totalSteps = 3;
  const progress = useMemo(() => Math.round((step / totalSteps) * 100), [step]);

  const setScore = (key, val) => setForm((f) => ({ ...f, scores: { ...f.scores, [key]: val } }));

  const canNext = () => {
    if (step === 0) return form.full_name.trim() && form.age && form.disability_category;
    if (step === 1) return true;
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        age: parseInt(form.age, 10) || 0,
        scores: Object.fromEntries(Object.entries(form.scores).map(([k, v]) => [k, parseInt(v, 10) || 5])),
        academy_preference: form.academy_preference || undefined,
        language: lang,
      };
      const { data } = await api.post('/talent/assess', payload);
      setResult(data);
      setStep(totalSteps);
      toast.success(isAr ? 'تم تحليل ملفّك' : 'Profile analysed');
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'تعذّر إرسال التقييم' : 'Submission failed'));
    }
    setBusy(false);
  };

  return (
    <main id="main" className="bg-gradient-to-b from-white via-amber-50/30 to-white min-h-screen py-8 px-4" data-testid="talent-assessment">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6 text-center">
          <Badge className="mb-3 text-xs px-3 py-1 font-bold bg-amber-500/15 text-amber-700 border-amber-300">
            <Brain className="w-3 h-3 me-1" />{isAr ? 'تقييم المواهب الذكي' : 'Smart Talent Assessment'}
          </Badge>
          <h1 className="text-2xl sm:text-4xl font-black mb-2">{isAr ? 'اكتشف رياضتك المثاليّة' : 'Discover your perfect sport'}</h1>
          <p className="text-sm text-muted-foreground">{isAr ? '٣ خطوات سريعة و سنوصي بأنسب رياضة لك' : '3 quick steps and we will recommend the best sport for you'}</p>
        </div>

        {step < totalSteps && (
          <Progress value={progress} className="mb-6 h-2" data-testid="ta-progress" />
        )}

        <Card className="card-elevate">
          <CardContent className="p-6 sm:p-8">
            {step === 0 && (
              <div className="space-y-4" data-testid="ta-step-personal">
                <h2 className="text-xl font-black mb-2">{isAr ? '١) معلومات شخصية' : '1) Personal information'}</h2>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">{isAr ? 'الاسم الكامل *' : 'Full name *'}</Label>
                    <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="ta-name" />
                  </div>
                  <div>
                    <Label className="text-xs">{isAr ? 'العمر *' : 'Age *'}</Label>
                    <Input type="number" min={5} max={70} value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} data-testid="ta-age" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">{isAr ? 'الجنس' : 'Gender'}</Label>
                    <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                      <SelectTrigger data-testid="ta-gender"><SelectValue placeholder={isAr ? 'اختر' : 'Select'} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">{isAr ? 'ذكر' : 'Male'}</SelectItem>
                        <SelectItem value="female">{isAr ? 'أنثى' : 'Female'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">{isAr ? 'الأكاديمية المفضّلة' : 'Preferred academy'}</Label>
                    <Select value={form.academy_preference} onValueChange={(v) => setForm({ ...form, academy_preference: v })}>
                      <SelectTrigger data-testid="ta-academy"><SelectValue placeholder={isAr ? 'اختر' : 'Select'} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="paralympic">{isAr ? 'بارالمبية' : 'Paralympic'}</SelectItem>
                        <SelectItem value="special-olympics">{isAr ? 'الأولمبياد الخاص' : 'Special Olympics'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">{isAr ? 'نوع الإعاقة *' : 'Disability category *'}</Label>
                  <Select value={form.disability_category} onValueChange={(v) => setForm({ ...form, disability_category: v })}>
                    <SelectTrigger data-testid="ta-disability"><SelectValue placeholder={isAr ? 'اختر الفئة' : 'Select category'} /></SelectTrigger>
                    <SelectContent>
                      {DISABILITY_OPTS.map((d) => (
                        <SelectItem key={d.id} value={d.id}>{isAr ? d.ar : d.en}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{isAr ? 'تفاصيل الحالة (اختياري)' : 'Condition details (optional)'}</Label>
                  <Textarea rows={2} value={form.disability_details} onChange={(e) => setForm({ ...form, disability_details: e.target.value })} data-testid="ta-details" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">{isAr ? 'الهاتف' : 'Phone'}</Label>
                    <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="ta-phone" />
                  </div>
                  <div>
                    <Label className="text-xs">{isAr ? 'البريد' : 'Email'}</Label>
                    <Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="ta-email" />
                  </div>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-5" data-testid="ta-step-scores">
                <h2 className="text-xl font-black mb-2">{isAr ? '٢) قيّم قدراتك (١-١٠)' : '2) Rate your abilities (1-10)'}</h2>
                <p className="text-xs text-muted-foreground mb-4">{isAr ? 'كلّما زاد الرقم زادت قدرتك في هذا المحور.' : 'A higher number = stronger ability in that axis.'}</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {SCORE_AXES.map((axis) => {
                    const Icon = axis.icon;
                    const val = form.scores[axis.key];
                    return (
                      <div key={axis.key} className="bg-stone-50 rounded-xl p-3 border border-border" data-testid={`ta-axis-${axis.key}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 text-amber-600" />
                            <span className="text-sm font-bold">{isAr ? axis.ar : axis.en}</span>
                          </div>
                          <Badge variant="outline" className="text-[11px] font-mono font-bold">{val}/10</Badge>
                        </div>
                        <Slider
                          min={1} max={10} step={1}
                          value={[val]}
                          onValueChange={(v) => setScore(axis.key, v[0])}
                          data-testid={`ta-slider-${axis.key}`}
                        />
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4">
                  <Label className="text-xs">{isAr ? 'أي رياضة تحب أن تجرّبها؟ (اختياري)' : 'Any sport you would like to try? (optional)'}</Label>
                  <Input value={form.sport_interest} onChange={(e) => setForm({ ...form, sport_interest: e.target.value })} data-testid="ta-interest" />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="text-center py-4" data-testid="ta-step-review">
                <Sparkles className="w-12 h-12 mx-auto text-amber-500 mb-3" />
                <h2 className="text-xl font-black mb-2">{isAr ? '٣) جاهز للتحليل؟' : '3) Ready to analyse?'}</h2>
                <p className="text-sm text-muted-foreground mb-6">
                  {isAr ? 'سنحلّل قدراتك ونوصي بأفضل الرياضات الملائمة.' : 'We will analyse your scores and recommend the best matching sports.'}
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-start text-sm space-y-1">
                  <div><strong>{isAr ? 'الاسم:' : 'Name:'}</strong> {form.full_name} · {form.age}</div>
                  <div><strong>{isAr ? 'الإعاقة:' : 'Disability:'}</strong> {DISABILITY_OPTS.find((d) => d.id === form.disability_category)?.[isAr ? 'ar' : 'en']}</div>
                  {form.academy_preference && (
                    <div><strong>{isAr ? 'الأكاديمية:' : 'Academy:'}</strong> {form.academy_preference}</div>
                  )}
                </div>
              </div>
            )}

            {step === totalSteps && result && (
              <div data-testid="ta-step-result">
                <div className="text-center mb-6">
                  <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-600 mb-2" />
                  <h2 className="text-2xl font-black mb-1">{isAr ? 'تم التحليل ✓' : 'Analysis complete ✓'}</h2>
                  <p className="text-sm text-muted-foreground">
                    {isAr ? 'هذه أفضل الرياضات الملائمة لك:' : 'Here are your top sport matches:'}
                  </p>
                </div>

                {/* AI narrative */}
                {result.ai_narrative && (
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-5" data-testid="ta-narrative">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-amber-600" />
                      <h3 className="font-bold text-sm">{isAr ? 'تحليل الذكاء الاصطناعي' : 'AI guidance'}</h3>
                    </div>
                    <p className="text-sm leading-relaxed">{result.ai_narrative[isAr ? 'summary_ar' : 'summary_en'] || result.ai_narrative.summary_ar}</p>
                    {result.ai_narrative[isAr ? 'recommended_ar' : 'recommended_en'] && (
                      <div className="mt-3 text-sm">
                        <strong>{isAr ? 'الرياضة الموصى بها: ' : 'Recommended sport: '}</strong>
                        {result.ai_narrative[isAr ? 'recommended_ar' : 'recommended_en']}
                      </div>
                    )}
                    {Array.isArray(result.ai_narrative[isAr ? 'next_steps_ar' : 'next_steps_en']) && result.ai_narrative[isAr ? 'next_steps_ar' : 'next_steps_en'].length > 0 && (
                      <ul className="list-disc list-inside text-sm mt-2 space-y-1">
                        {result.ai_narrative[isAr ? 'next_steps_ar' : 'next_steps_en'].map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    )}
                  </div>
                )}

                {/* Top matches */}
                <div className="space-y-3" data-testid="ta-matches">
                  {(result.top_matches || []).map((m, i) => (
                    <div key={m.sport_id} className="bg-white border-2 border-border rounded-xl p-4 flex items-center gap-3 card-elevate" data-testid={`ta-match-${m.sport_id}`}>
                      <div className="w-10 h-10 rounded-lg bg-amber-100 grid place-items-center shrink-0 font-black text-amber-700">{i + 1}</div>
                      <div className="flex-1">
                        <div className="font-bold">{isAr ? m.name_ar : m.name_en}</div>
                        <div className="text-xs text-muted-foreground">{m.academy === 'both' ? (isAr ? 'بارالمبية / أولمبياد خاص' : 'Paralympic / Special Olympics') : m.academy}</div>
                      </div>
                      <div className="text-end">
                        <div className="text-xl font-black text-amber-700">{m.match_pct}%</div>
                        <div className="text-[10px] text-muted-foreground uppercase">{isAr ? 'تطابق' : 'match'}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex gap-2 flex-wrap">
                  <Button onClick={() => navigate(`/academy/training?academy=${form.academy_preference || 'paralympic'}&sport=${(result.top_matches?.[0]?.sport_id) || ''}`)} data-testid="ta-go-training" className="bg-emerald-600 hover:bg-emerald-700">
                    <Target className="w-4 h-4 me-2" />{isAr ? 'ابدأ خطة التدريب' : 'Start training plan'}
                  </Button>
                  {form.academy_preference === 'paralympic' && (
                    <Button variant="outline" onClick={() => navigate('/academy/classification')} data-testid="ta-go-classify">
                      <Trophy className="w-4 h-4 me-2" />{isAr ? 'التصنيف الفني' : 'Functional classification'}
                    </Button>
                  )}
                  <Button variant="ghost" onClick={() => { setStep(0); setResult(null); }} data-testid="ta-restart">
                    {isAr ? 'تقييم جديد' : 'New assessment'}
                  </Button>
                </div>
              </div>
            )}

            {/* Footer nav */}
            {step < totalSteps && (
              <div className="flex items-center justify-between mt-6">
                <Button variant="ghost" disabled={step === 0 || busy} onClick={() => setStep(step - 1)} data-testid="ta-prev">
                  <ChevronLeft className={`w-4 h-4 me-1 ${isAr ? 'rotate-180' : ''}`} />{isAr ? 'السابق' : 'Back'}
                </Button>
                {step < 2 ? (
                  <Button disabled={!canNext() || busy} onClick={() => setStep(step + 1)} data-testid="ta-next">
                    {isAr ? 'التالي' : 'Next'}
                    <ChevronRight className={`w-4 h-4 ms-1 ${isAr ? 'rotate-180' : ''}`} />
                  </Button>
                ) : (
                  <Button disabled={busy} onClick={submit} className="bg-amber-600 hover:bg-amber-700" data-testid="ta-submit">
                    {busy ? <><Loader2 className="w-4 h-4 me-2 animate-spin" />{isAr ? 'جاري التحليل...' : 'Analysing...'}</> : (
                      <><Sparkles className="w-4 h-4 me-2" />{isAr ? 'حلّل ملفّي' : 'Analyse my profile'}</>
                    )}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
